import React, { useState, useEffect, useCallback } from 'react';
import { Eye, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const VisitorsTab = () => {
  const [visitors, setVisitors] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [viewMode, setViewMode] = useState('overview'); // overview, list

  const fetchVisitorData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [visitorsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/visitors?limit=100`),
        axios.get(`${API}/admin/visitors/stats`)
      ]);
      setVisitors(visitorsRes.data.visitors || []);
      setStats(statsRes.data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching visitor data:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisitorData();
  }, [fetchVisitorData]);

  const clearAllData = async () => {
    if (!window.confirm('Are you sure you want to clear all visitor data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/visitors/clear`, {});
      fetchVisitorData();
    } catch (e) {
      alert('Error clearing data');
    }
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return '📱';
    if (deviceType === 'tablet') return '📟';
    return '🖥️';
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="text-slate-500">Loading visitor data...</div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          <Eye className="inline mr-2" size={24} />Visitor Analytics
        </h2>
        <div className="flex space-x-3">
          <button onClick={() => setViewMode('overview')} className={`px-4 py-2 rounded ${viewMode === 'overview' ? 'bg-orange-500 text-white' : 'bg-slate-100'}`}>Overview</button>
          <button onClick={() => setViewMode('list')} className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-orange-500 text-white' : 'bg-slate-100'}`}>Visitor List</button>
          <button onClick={fetchVisitorData} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Refresh</button>
          <button onClick={clearAllData} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">Clear Data</button>
        </div>
      </div>

      {viewMode === 'overview' && stats && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white border rounded-lg p-4">
              <p className="text-sm text-slate-500">Total Page Views</p>
              <p className="text-2xl font-bold">{stats.total_views?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-600">Unique Visitors</p>
              <p className="text-2xl font-bold text-blue-700">{stats.unique_visitors?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600">Sessions</p>
              <p className="text-2xl font-bold text-green-700">{stats.unique_sessions?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-600">Today's Views</p>
              <p className="text-2xl font-bold text-orange-700">{stats.today_views?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-600">This Week</p>
              <p className="text-2xl font-bold text-purple-700">{stats.week_views?.toLocaleString() || 0}</p>
            </div>
          </div>

          {/* Charts and Breakdowns */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Daily Views Chart */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Daily Views (Last 30 Days)</h3>
              {stats.daily_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={stats.daily_stats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="views" stroke="#f97316" name="Page Views" />
                    <Line type="monotone" dataKey="unique_visitors" stroke="#3b82f6" name="Unique Visitors" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No data yet</div>
              )}
            </div>

            {/* Device Breakdown */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Device Breakdown</h3>
              {stats.device_stats?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.device_stats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="_id" type="category" width={80} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No data yet</div>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Top Pages */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Top Pages</h3>
              <div className="space-y-3">
                {stats.top_pages?.map((page) => (
                  <div key={page._id || page.count} className="flex justify-between items-center">
                    <span className="text-sm truncate flex-1 mr-2">{page._id}</span>
                    <span className="text-sm font-bold bg-slate-100 px-2 py-1 rounded">{page.count}</span>
                  </div>
                ))}
                {(!stats.top_pages || stats.top_pages.length === 0) && <p className="text-slate-400 text-center">No data</p>}
              </div>
            </div>

            {/* Top Referrers */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Top Referrers</h3>
              <div className="space-y-3">
                {stats.top_referrers?.map((ref, idx) => (
                  <div key={ref._id || idx} className="flex justify-between items-center">
                    <span className="text-sm truncate flex-1 mr-2">{ref._id || 'Direct'}</span>
                    <span className="text-sm font-bold bg-green-100 text-green-700 px-2 py-1 rounded">{ref.count}</span>
                  </div>
                ))}
                {(!stats.top_referrers || stats.top_referrers.length === 0) && <p className="text-slate-400 text-center">No referrer data</p>}
              </div>
            </div>

            {/* Browsers */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Browsers</h3>
              <div className="space-y-3">
                {stats.browser_stats?.map((browser, idx) => (
                  <div key={browser._id || idx} className="flex justify-between items-center">
                    <span className="text-sm">{browser._id || 'Unknown'}</span>
                    <span className="text-sm font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded">{browser.count}</span>
                  </div>
                ))}
                {(!stats.browser_stats || stats.browser_stats.length === 0) && <p className="text-slate-400 text-center">No data</p>}
              </div>
            </div>
          </div>

          {/* Geographic Data */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Top Countries */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Top Countries</h3>
              <div className="space-y-3">
                {stats.top_countries?.map((country, idx) => (
                  <div key={country._id || idx} className="flex justify-between items-center">
                    <span className="text-sm">{country._id || 'Unknown'}</span>
                    <span className="text-sm font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded">{country.count}</span>
                  </div>
                ))}
                {(!stats.top_countries || stats.top_countries.length === 0) && <p className="text-slate-400 text-center">No location data</p>}
              </div>
            </div>

            {/* Top Cities */}
            <div className="bg-white border rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Top Cities</h3>
              <div className="space-y-3">
                {stats.top_cities?.map((city, idx) => (
                  <div key={city._id?.city || idx} className="flex justify-between items-center">
                    <span className="text-sm">{city._id?.city}, {city._id?.region}</span>
                    <span className="text-sm font-bold bg-orange-100 text-orange-700 px-2 py-1 rounded">{city.count}</span>
                  </div>
                ))}
                {(!stats.top_cities || stats.top_cities.length === 0) && <p className="text-slate-400 text-center">No city data</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {viewMode === 'list' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4">Time</th>
                  <th className="text-left py-3 px-4">Visitor</th>
                  <th className="text-left py-3 px-4">Page</th>
                  <th className="text-left py-3 px-4">Location</th>
                  <th className="text-left py-3 px-4">Device</th>
                  <th className="text-left py-3 px-4">Browser</th>
                  <th className="text-left py-3 px-4">Referrer</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => (
                  <tr key={v.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedVisitor(v)}>
                    <td className="py-3 px-4 text-xs">{formatDate(v.timestamp)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        {v.is_new_visitor ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded mr-2">NEW</span>
                        ) : (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mr-2">Returning</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 font-medium">{v.page_path}</td>
                    <td className="py-3 px-4">
                      <div>{v.city || '-'}</div>
                      <div className="text-xs text-slate-500">{v.region}, {v.country}</div>
                    </td>
                    <td className="py-3 px-4">{getDeviceIcon(v.device_type)} {v.device_type}</td>
                    <td className="py-3 px-4">{v.browser} / {v.os}</td>
                    <td className="py-3 px-4 text-xs truncate max-w-[150px]">{v.referrer_domain || 'Direct'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visitors.length === 0 && <p className="text-center text-slate-500 py-8">No visitor data yet. Visitors will appear here once they accept cookies and browse your site.</p>}
          </div>
        </div>
      )}

      {/* Visitor Detail Modal */}
      {selectedVisitor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-slate-800 text-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold">Visitor Details</h3>
                  <p className="text-slate-300 text-sm">{formatDate(selectedVisitor.timestamp)}</p>
                </div>
                <button onClick={() => setSelectedVisitor(null)} className="text-slate-400 hover:text-white"><X size={24} /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Location Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded">
                  <h4 className="font-semibold mb-2">Location</h4>
                  <p><span className="text-slate-500">IP:</span> {selectedVisitor.ip_address || 'N/A'}</p>
                  <p><span className="text-slate-500">City:</span> {selectedVisitor.city || 'N/A'}</p>
                  <p><span className="text-slate-500">Region:</span> {selectedVisitor.region || 'N/A'}</p>
                  <p><span className="text-slate-500">Country:</span> {selectedVisitor.country || 'N/A'}</p>
                  <p><span className="text-slate-500">Timezone:</span> {selectedVisitor.timezone || 'N/A'}</p>
                  <p><span className="text-slate-500">ISP:</span> {selectedVisitor.isp || 'N/A'}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded">
                  <h4 className="font-semibold mb-2">Device & Browser</h4>
                  <p><span className="text-slate-500">Device:</span> {selectedVisitor.device_type}</p>
                  <p><span className="text-slate-500">Browser:</span> {selectedVisitor.browser}</p>
                  <p><span className="text-slate-500">OS:</span> {selectedVisitor.os}</p>
                  <p><span className="text-slate-500">Screen:</span> {selectedVisitor.screen_width}x{selectedVisitor.screen_height}</p>
                  <p><span className="text-slate-500">Viewport:</span> {selectedVisitor.viewport_width}x{selectedVisitor.viewport_height}</p>
                  <p><span className="text-slate-500">Language:</span> {selectedVisitor.language}</p>
                </div>
              </div>
              {/* Page Info */}
              <div className="bg-slate-50 p-4 rounded">
                <h4 className="font-semibold mb-2">Page Visit</h4>
                <p><span className="text-slate-500">Page:</span> {selectedVisitor.page_path}</p>
                <p><span className="text-slate-500">Title:</span> {selectedVisitor.page_title || 'N/A'}</p>
                <p><span className="text-slate-500">Full URL:</span> <span className="break-all">{selectedVisitor.page_url}</span></p>
                <p><span className="text-slate-500">Referrer:</span> {selectedVisitor.referrer || 'Direct'}</p>
                <p><span className="text-slate-500">Pages in Session:</span> {selectedVisitor.pages_in_session}</p>
              </div>
              {/* UTM Data */}
              {(selectedVisitor.utm_source || selectedVisitor.utm_medium || selectedVisitor.utm_campaign) && (
                <div className="bg-green-50 p-4 rounded border border-green-200">
                  <h4 className="font-semibold mb-2 text-green-700">Campaign Tracking (UTM)</h4>
                  <p><span className="text-slate-500">Source:</span> {selectedVisitor.utm_source || 'N/A'}</p>
                  <p><span className="text-slate-500">Medium:</span> {selectedVisitor.utm_medium || 'N/A'}</p>
                  <p><span className="text-slate-500">Campaign:</span> {selectedVisitor.utm_campaign || 'N/A'}</p>
                  <p><span className="text-slate-500">Term:</span> {selectedVisitor.utm_term || 'N/A'}</p>
                  <p><span className="text-slate-500">Content:</span> {selectedVisitor.utm_content || 'N/A'}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end p-6 border-t">
              <button onClick={() => setSelectedVisitor(null)} className="px-6 py-2 border rounded">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Commissions Tab Component with Comprehensive Sales Rep Management

export default VisitorsTab;
